<script lang="ts">
  import type { Maybe, Place, PlaceType, PlaceHoursGroup } from "../../types";
  import Alert from "../../components/alert.svelte";
  import Description from "../../components/description.svelte";
  import Hero from "../../components/hero.svelte";
  import MapDrawer from "../../components/map-drawer.svelte";
  import Schedule from "../../components/places/schedule.svelte";
  import { afterNavigate } from "$app/navigation";
  import { getStatus, getStatusNote, isClosedStatus } from "../../domain/status";
  import { isDisplayed } from "../../domain/status";
  import { placePath, type QueryParams } from "../../support/links";
  import { page } from "$app/stores";
  import { parseId } from "../../params/slug";
  import { getNow, getTimeZone } from "../../data/clock";
  import { reify } from "../../domain/hours";
  import Locale from "../locale.svelte";

  export let time = getNow();
  export let timeZone = getTimeZone();
  export let place: Place;
  export let places: Place[];
  export let placeTypes: PlaceType[];
  export let parent: Maybe<Place>;
  export let children: Place[];
  export let placeHoursGroup: PlaceHoursGroup | null;

  $: query = $page.url.searchParams;
  $: activePlaceId = parseId(query.get("place"));

  $: scheduleStart = $time;
  $: placeHours = placeHoursGroup ? placeHoursGroup.placeHours : [];

  $: schedule = placeHours.flatMap((h) => {
    return reify(h, scheduleStart, 6, $timeZone);
  });

  $: describedPlaceTypes = placeTypes.filter((type) => type.description);
  $: status = getStatus(place, $time);
  $: closed = isClosedStatus(status);
  $: statusNote = getStatusNote(place, $time);
  $: displayedChildren = children.filter((place) => isDisplayed(place, $time));

  function mapLinkBuilder(query: QueryParams) {
    return placePath(place, query);
  }

  let main: HTMLElement;

  // We shouldn't need this....
  // https://github.com/sveltejs/kit/issues/2733
  afterNavigate((_navigation) => {
    if (main) {
      main.scrollTo(0, 0);
    }
  });
</script>

<main class="Container" bind:this={main}>
  <Hero src={place.graphic} alt="Photo of {place.title}" />

  {#if closed}
    <Alert label="status.{status}" subtitle={statusNote} />
  {:else if statusNote}
    <Alert color="var(--color-status-green)" subtitle={statusNote} />
  {/if}

  <Schedule {place} {schedule} {time} />

  {#if place.description}
    <Description text={place.description} />
  {:else}
    {#each describedPlaceTypes as placeType}
      {#if placeType.description}
        <Description text={placeType.description} />
      {/if}
    {:else}
      <!-- default description -->
      <Description />
    {/each}
  {/if}

  {#if parent && isDisplayed(parent, $time)}
    <section class="Section">
      <h2 class="SectionTitle">
        <Locale label="places.nearby" />
      </h2>
      <p><a href={placePath(parent)}>{parent.title}</a></p>
    </section>
  {/if}

  {#if displayedChildren.length}
    <section class="Section">
      <h2 class="SectionTitle">
        <Locale label="places.explore" />
      </h2>
      <ul class="Children">
        {#each displayedChildren as child}
          <li><a href={placePath(child)}>{child.title}</a></li>
        {/each}
      </ul>
    </section>
  {/if}

  {#if place.mapId}
    <MapDrawer {activePlaceId} {places} mapId={place.mapId} linkBuilder={mapLinkBuilder} />
  {/if}
</main>

<style>
  .Container {
    flex: 1 1 auto;
    hyphens: auto;
    overflow: auto;
    padding-bottom: 48px;
    position: relative;
    width: 100%;
  }

  .Section {
    hyphens: auto;
    margin-block: var(--outer-padding);
    margin-left: max(env(safe-area-inset-left), var(--outer-padding));
    margin-right: max(env(safe-area-inset-right), var(--outer-padding));
  }

  .SectionTitle {
    font-size: calc(var(--sp) * 16);
    letter-spacing: calc(var(--sp) * 0.5);
    font-weight: 600;
  }

  .Children {
    margin: var(--outer-padding) 0;
    padding: 0;
    list-style: none;
  }

  .Children li {
    margin: 8px 0;
    list-style: none;
    padding-bottom: 4px;
  }
</style>
